import { useEffect, useMemo, useState } from 'react';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import { useMutation, useQuery } from '@tanstack/react-query';
import { ButtonNew } from '@/components-new/ButtonNew/ButtonNew';
import { Box } from '@/components/Box/Box';
import { Typography } from '@/components/Typography/Typography';
import { Notification } from '@/components/Notification/Notification';
import { FormikForm } from '@/components/form/FormikForm/FormikForm';
import { FormikInput } from '@/components/form/fields/FormikInput/FormikInput';
import { FormikRadioGroup } from '@/components/form/fields/FormikRadioGroup/FormikRadioGroup';
import { getResponseError } from '@/helpers/apiHelpers/responseHelpers';
import { amplitudeService } from '@/services/amplitudeService/amplitudeService';
import { AMPLITUDE_EVENTS } from '@/services/amplitudeService/amplitudeEvents';
import { CreateInvoiceCheckPayloadType } from '@/api/v1/organization/invoice-vault/createInvoiceCheckApi';
import { VENDOR_VAULT_QUERY_KEYS, loadVendorVaultCatalog } from '@/bundle/_OrgAdmin/pages/VendorVaultPage/api/api';
import { CHECK_EXPIRY_OPTIONS, CHECK_MODE_CARDS, CHECK_TYPE_CARDS } from '../../../const/const';
import { createInvoiceCheck } from '../../../api/api';
import { Steps } from '../../shared/Steps/Steps';
import { InvoiceDropZone } from './InvoiceDropZone';
import { DialogButtons } from '@/components-new/Dialog/styles';
import { InputError } from '@/components/form/fields/InputError/InputError';
import { FormikSelectableTabs } from '@/components/form/fields/FormikSelectableTabs/FormikSelectableTabs';
import { FormikChips } from '@/components/form/fields/FormikChips/FormikChips';
import { showSnackbar } from '@/components/Snackbar/Snackbar';
import {
  getBankOptions,
  getTemplateFields,
  getTemplateLabel,
  isDraftTemplate,
  needsBankTemplate,
} from '@/bundle/_OrgAdmin/pages/VendorVaultPage/helpers/catalogHelpers';
import { RequestTypeType } from '@/types/vendorVaultTypes';
import { HighlightPanel } from '@/components-new/HighlightPanel/styles';
import { OptionGrid } from '@/bundle/_OrgAdmin/pages/MyVaultPage/styles';
import { FormikSwitchToggle } from '@/components/form/fields/FormikSwitchToggle/FormikSwitchToggle';
import { COLORS } from '@/styles/colors';
import { FormikPhoneInput } from '@/components/form/fields/FormikPhoneInput/FormikPhoneInput';

const STEP_LABELS = ['Upload invoice', 'Vendor Vault', 'Send request'];

type FormValues = {
  invoice_file_key: string;
  invoice_file_name: string;
  vendor_name: string;
  invoice_number: string;
  amount: string;
  currency: string;
  bank_name_on_invoice: string;
  account_number_on_invoice: string;
  routing_on_invoice: string;
  request_type: RequestTypeType | '';
  bank: string | null;
  detailed: boolean;
  mode: 'request' | 'verify' | '';
  audience: 'recipient' | 'proxy';
  via: 'email' | 'sms';
  vendor_contact: string;
  expiry_code: '24h' | '7d' | '30d';
  wire_confirmation: boolean;
};

const INITIAL_VALUES: FormValues = {
  invoice_file_key: '',
  invoice_file_name: '',
  vendor_name: '',
  invoice_number: '',
  amount: '',
  currency: 'USD',
  bank_name_on_invoice: '',
  account_number_on_invoice: '',
  routing_on_invoice: '',
  request_type: '',
  bank: null,
  detailed: false,
  mode: 'verify',
  audience: 'recipient',
  via: 'email',
  vendor_contact: '',
  expiry_code: '7d',
  wire_confirmation: false,
};

const STEP_FIELDS: Record<number, (keyof FormValues)[]> = {
  1: [
    'invoice_file_key',
    'vendor_name',
    'invoice_number',
    'amount',
    'currency',
    'bank_name_on_invoice',
    'account_number_on_invoice',
  ],
  2: ['request_type', 'bank', 'mode'],
  3: ['vendor_contact', 'via', 'expiry_code'],
};

const SEND_BY_OPTIONS = [
  { value: 'email', label: 'Email' },
  { value: 'sms', label: 'SMS' },
];

const RECIPIENT_OPTIONS = [
  {
    value: 'recipient',
    label: 'Vendor',
    description: 'The vendor confirms the invoice and enters their own details.',
  },
  {
    value: 'proxy',
    label: 'Proxy',
    description: 'This person can invite the vendor, or enter the details on their behalf.',
  },
];

const schema = Yup.object({
  invoice_file_key: Yup.string().required('Upload the invoice first.'),
  vendor_name: Yup.string().trim().required('Vendor name is required.').max(255),
  invoice_number: Yup.string().trim().required('Invoice number is required.').max(64),
  amount: Yup.string()
    .trim()
    .required('Amount is required.')
    .matches(/^\d+(\.\d{1,2})?$/, 'Enter an amount like 18400.00'),
  currency: Yup.string().trim().length(3, 'Three-letter code, like USD.').required('Currency is required.'),
  bank_name_on_invoice: Yup.string().trim().required('Bank name as printed on the invoice.').max(255),
  account_number_on_invoice: Yup.string().trim().required('Account number as printed on the invoice.').max(64),
  routing_on_invoice: Yup.string().trim().max(64),
  request_type: Yup.string().required('Choose the payment type.'),
  // same rule as the Vendor Vault request: a bank template only for international
  bank: Yup.string()
    .nullable()
    .when('request_type', { is: 'intl', then: (s) => s.required('Choose the bank you will send from.') }),
  mode: Yup.string().required('Choose how the vendor responds.'),
  via: Yup.string().required(),
  vendor_contact: Yup.string().when('via', {
    is: 'email',
    then: (s) => s.trim().email('That email address does not look right.').required('Enter an email address.'),
    otherwise: (s) => s.trim().required('Enter a mobile number.'),
  }),
  expiry_code: Yup.string().required(),
});

export const CheckInvoiceWizard = ({ onSent }: { onSent: () => void }) => {
  const [step, setStep] = useState(1);
  const [apiError, setApiError] = useState('');
  const { data: catalogResponse } = useQuery({
    queryKey: VENDOR_VAULT_QUERY_KEYS.catalog,
    queryFn: loadVendorVaultCatalog,
    staleTime: Infinity,
  });
  const catalog = catalogResponse?.body;

  const send = useMutation({
    mutationFn: createInvoiceCheck,
    onSuccess(response) {
      if (response.error) return setApiError(getResponseError(response.error));

      showSnackbar('Check invoice sent successfully.');

      amplitudeService.logEvent(AMPLITUDE_EVENTS.InvoiceCheckSent);
      setStep(1);
      formik.resetForm();
      onSent();
    },
  });

  const formik = useFormik<FormValues>({
    initialValues: INITIAL_VALUES,
    validationSchema: schema,
    onSubmit: (values) => {
      const isIntl = needsBankTemplate(values.request_type);
      const payload: CreateInvoiceCheckPayloadType = {
        ...values,
        request_type: values.request_type as RequestTypeType,
        mode: values.mode as 'request' | 'verify',
        vendor_name: values.vendor_name.trim(),
        invoice_number: values.invoice_number.trim(),
        currency: values.currency.trim().toUpperCase(),
        vendor_contact: values.vendor_contact.trim(),
        bank: isIntl ? values.bank : null,
        detailed: isIntl ? values.detailed : false,
        wire_confirmation: values.request_type === 'ach' ? false : values.wire_confirmation,
      };

      send.mutate(payload);
    },
  });

  useEffect(() => {
    formik.setFieldValue('bank', '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formik.values.request_type]);

  // a bank template belongs to intl only; clear it (and the intl-only toggle) when the type changes
  useEffect(() => {
    if (!isIntl && (values.bank !== null || values.detailed)) {
      formik.setFieldValue('bank', null);
      formik.setFieldValue('detailed', false);
      formik.setFieldTouched('bank', false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formik.values.request_type]);

  // email and phone are different inputs; do not carry one into the other
  useEffect(() => {
    formik.setFieldValue('vendor_contact', '');
    formik.setFieldTouched('vendor_contact', false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formik.values.via]);

  const next = async () => {
    const errors = await formik.validateForm();
    const blocking = STEP_FIELDS[step].filter((key) => errors[key]);

    if (blocking.length) {
      formik.setTouched(Object.fromEntries(blocking.map((key) => [key, true])), true);

      return;
    }

    setStep((s) => s + 1);
  };

  const { values } = formik;
  const isIntl = needsBankTemplate(values.request_type);

  const bankOptions = useMemo(
    () => (catalog ? getBankOptions(catalog, values.request_type) : []),
    [catalog, values.request_type]
  );
  const templateFields = useMemo(
    () => (catalog ? getTemplateFields(catalog, values.request_type, values.bank, values.detailed) : null),
    [catalog, values.request_type, values.bank, values.detailed]
  );
  const templateLabel = catalog ? getTemplateLabel(catalog, values.request_type, values.bank) : '';

  const hasFile = !!values.invoice_file_key;

  return (
    <Box>
      <Typography variant='bodySmall' marginBottom='20px'>
        Upload the invoice, pick the payment type, and we ask the vendor to confirm it is theirs and enter their payout
        details. We compare what they enter with what is printed on the invoice and anchor the result.
      </Typography>
      <Steps labels={STEP_LABELS} current={step} />
      <FormikForm value={formik}>
        {step === 1 && (
          <Box display='flex' flexDirection='column' gap='20px'>
            <InvoiceDropZone
              fileName={values.invoice_file_name}
              onUploaded={({ file_key, file_name }) =>
                formik.setValues({ ...values, invoice_file_key: file_key, invoice_file_name: file_name })
              }
              onError={setApiError}
            />
            {formik.touched.invoice_file_key && formik.errors.invoice_file_key && (
              <InputError error={formik.errors.invoice_file_key} />
            )}

            {hasFile && (
              <Box marginTop='12px'>
                <Notification variant='info' marginBottom='16px'>
                  Type what is printed on the invoice. We only keep a hash of the account number; the vendor never sees
                  these values.
                </Notification>

                <Box display='grid' gridTemplateColumns='1fr 1fr' columnGap='20px'>
                  <FormikInput
                    name='vendor_name'
                    label='Vendor name*'
                    placeholder='Okafor Imports Ltd'
                    autoComplete='new-field'
                  />
                  <FormikInput
                    name='invoice_number'
                    label='Invoice number*'
                    placeholder='OI-2026-0917'
                    autoComplete='new-field'
                  />
                  <FormikInput name='amount' label='Amount*' placeholder='18400.00' />
                  <FormikInput name='currency' label='Currency*' placeholder='USD' />
                  <FormikInput
                    name='bank_name_on_invoice'
                    label='Bank on the invoice*'
                    placeholder='First Atlantic Bank'
                    autoComplete='new-field'
                  />
                  <FormikInput
                    name='account_number_on_invoice'
                    label='Account number on the invoice*'
                    placeholder='7700 3391'
                    autoComplete='new-field'
                  />
                  <FormikInput
                    name='routing_on_invoice'
                    label='Routing / SWIFT on the invoice'
                    placeholder='267084131'
                    autoComplete='new-field'
                  />
                </Box>
              </Box>
            )}
          </Box>
        )}

        {step === 2 && (
          <Box display='flex' flexDirection='column'>
            <FormikSelectableTabs
              name='request_type'
              options={CHECK_TYPE_CARDS}
              label={<b>How will you pay this vendor?</b>}
              direction='row'
              hasErrorComponent
            />

            {isIntl && (
              <FormikChips
                name='bank'
                label={<b>Which bank will you send from?</b>}
                options={bankOptions}
                direction='column'
                apiError={send.data?.error}
              />
            )}

            {templateFields && (
              <HighlightPanel margin='0 0 22px'>
                <Box display='flex' justifyContent='space-between' marginBottom='4px'>
                  <Typography variant='bodySmall' fontWeight='semibold'>
                    Fields the vendor will fill{isIntl ? ` (${templateLabel} template)` : ''}
                  </Typography>
                  {catalog && isDraftTemplate(catalog, values.request_type, values.bank) && (
                    <Typography variant='caption'>Draft template</Typography>
                  )}
                </Box>
                <OptionGrid $cols={2} $gap='2px'>
                  {templateFields.map((field, index) => (
                    <Typography key={field.key} variant='bodySmall'>
                      {index + 1}. {field.label}
                    </Typography>
                  ))}
                </OptionGrid>
              </HighlightPanel>
            )}

            {isIntl && (
              <HighlightPanel margin='0 0 22px'>
                <FormikSwitchToggle
                  name='detailed'
                  label={<b>Detailed information needed?</b>}
                  hasErrorComponent={false}
                  hint={
                    <Typography variant='bodySmall' color={COLORS.grey[650]}>
                      Adds intermediary bank, local clearing code and a supporting document to the form.
                    </Typography>
                  }
                />
              </HighlightPanel>
            )}

            <FormikSelectableTabs
              name='mode'
              options={CHECK_MODE_CARDS}
              label={<b>How should the vendor respond?</b>}
              direction='row'
              hasErrorComponent
            />
          </Box>
        )}

        {step === 3 && (
          <Box display='flex' flexDirection='column'>
            <FormikSelectableTabs
              name='audience'
              label='The payout details will be provided by'
              direction='row'
              options={RECIPIENT_OPTIONS}
              hasErrorComponent
            />
            <Box marginBottom='12px'>
              <FormikRadioGroup name='via' label='Send by' options={SEND_BY_OPTIONS} />
            </Box>
            {values.via === 'email' ? (
              <FormikInput
                name='vendor_contact'
                label='Email address*'
                placeholder='Vendor email address'
                autoComplete='new-field'
                apiError={send.data?.error}
              />
            ) : (
              <FormikPhoneInput name='vendor_contact' label='Mobile number*' apiError={send.data?.error} />
            )}
            <FormikRadioGroup
              name='expiry_code'
              label='Request expires after'
              options={CHECK_EXPIRY_OPTIONS}
              hasErrorComponent
            />
            {values.request_type !== 'ach' && (
              <FormikSwitchToggle
                name='wire_confirmation'
                label={<b>Send the vendor a wire confirmation after you pay?</b>}
                hasErrorComponent={false}
              />
            )}
            <Typography variant='bodySmall2'>
              The vendor first answers "Is this your invoice?" for {values.invoice_number || 'the invoice'}
              {values.amount ? ` (${values.currency} ${values.amount})` : ''}. If they say no, the request closes and
              you are alerted.
            </Typography>
          </Box>
        )}

        {apiError && (
          <Notification marginTop='14px' variant='error'>
            {apiError}
          </Notification>
        )}

        <DialogButtons>
          {step > 1 && (
            <ButtonNew
              variant='secondary'
              size='medium'
              onClick={step === 1 ? null : () => setStep((stepIndex) => stepIndex - 1)}
            >
              {step === 1 ? 'Cancel' : 'Back'}
            </ButtonNew>
          )}

          {step < 3 ? (
            <ButtonNew size='medium' onClick={next} disabled={!hasFile}>
              Continue
            </ButtonNew>
          ) : (
            <ButtonNew size='medium' type='submit' isLoading={send.isPending}>
              Send request
            </ButtonNew>
          )}
        </DialogButtons>
      </FormikForm>
    </Box>
  );
};
